export default class ProductListing {
    constructor(lenis = null) {
        this.lenis = lenis;
        this.loadMoreBtn = null;
        this.productList = null;
        this.paginationInfo = null;
        this.paginationProgress = null;
        this.isLoading = false;
        this.loader = null;

        // AJAX 사용 여부 (기본값: true, false면 기존 DOM 요소만 opacity 1로 처리)
        this.useAjax = true;

        // 현재 상태 관리
        this.currentBoardNo = "1";
        this.currentCategory = "";
        this.currentCursor = 0;
        this.currentLoaded = 0;
        this.total = 0;

        this.init();
    }

    /**
     * 초기화
     * DOM 요소 선택 및 이벤트 바인딩
     */
    init() {
        console.log("[ProductListing] init()");

        // DOM 요소 선택
        this.loadMoreBtn = document.querySelector(
            ".no-products__pagination-more"
        );
        this.productList = document.querySelector(".no-products__list");
        this.paginationInfo = document.querySelector(
            ".no-products__pagination-info"
        );
        this.paginationProgress = document.querySelector(
            ".no-products__pagination-progress"
        );
        this.loader = document.getElementById("products-loader");

        // 필수 요소 확인
        if (!this.productList) {
            console.warn("[ProductListing] Product list not found");
            return;
        }

        // 카테고리 이벤트 바인딩
        this.bindCategoryEvents();

        // 초기 제품 로드
        this.loadInitialProducts();
    }

    /**
     * 초기 제품 로드
     * URL 파라미터에 cursor와 loaded가 있으면 복원, 없으면 첫 페이지 로드
     */
    async loadInitialProducts() {
        // AJAX를 사용하지 않는 경우, 기존 DOM 요소만 opacity 1로 처리
        if (!this.useAjax) {
            console.log(
                "[ProductListing] useAjax is false, setting existing items to opacity 1"
            );
            this.setExistingItemsOpacity();
            return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const cursor = urlParams.get("cursor");
        const loaded = urlParams.get("loaded");

        // cursor와 loaded가 있으면 이전 상태 복원
        if (cursor && loaded) {
            console.log(
                `[ProductListing] Restoring previous state: cursor=${cursor}, loaded=${loaded}`
            );
            await this.restorePreviousState();
        } else {
            // 첫 페이지 로드
            console.log("[ProductListing] Loading first page");
            await this.loadFirstPage();
        }
    }

    /**
     * 첫 페이지 로드
     * cursor=0, loaded=0으로 API 호출하여 첫 12개 제품 로드
     */
    async loadFirstPage() {
        const urlParams = new URLSearchParams(window.location.search);
        const boardNo = urlParams.get("board_no") || "1";
        const category = urlParams.get("category") || "";

        // 현재 상태 업데이트
        this.currentBoardNo = boardNo;
        this.currentCategory = category;
        this.currentCursor = 0;
        this.currentLoaded = 0;

        try {
            this.setLoadingState(true);
            this.showLoader();

            // API 호출: cursor=0, loaded=0
            const apiUrl = `/api/product.php?board_no=${boardNo}&cursor=0&loaded=0${
                category ? "&category=" + category : ""
            }`;

            const response = await fetch(apiUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || "Failed to load products");
            }

            // 기존 리스트 비우기
            this.productList.innerHTML = "";

            // 제품 렌더링
            if (data.products && data.products.length > 0) {
                const newItems = [];
                data.products.forEach((product) => {
                    const item = this.createProductItem(product, boardNo);
                    this.productList.appendChild(item);
                    newItems.push(item);
                });

                // 애니메이션
                if (newItems.length > 0) {
                    this.animateNewItems(newItems);
                }
            }

            // 상태 업데이트
            this.currentCursor = parseInt(data.next_cursor || "0", 10);
            this.currentLoaded = parseInt(data.current_loaded || "0", 10);
            this.total = parseInt(data.total || "0", 10);

            // 페이지네이션 정보 업데이트
            this.updatePagination(data);

            // 필터 제목 업데이트
            this.updateFilterTitle(data.total);

            // 더보기 버튼 업데이트
            this.updateLoadMoreButton(data, boardNo, category);

            // 카테고리 활성 상태 업데이트
            this.updateCategoryActiveState(category);

            // URL 업데이트 (히스토리 변경 없이 현재 상태 반영)
            this.updateURL(
                boardNo,
                this.currentCursor,
                this.currentLoaded,
                category
            );
        } catch (error) {
            console.error("[ProductListing] Error loading first page:", error);
            alert(
                "Fehler beim Laden der Produkte. Bitte versuchen Sie es erneut."
            );
        } finally {
            this.setLoadingState(false);
            this.hideLoader();
        }
    }

    /**
     * 더보기 버튼 클릭 시 다음 페이지 로드
     */
    async loadMore() {
        if (this.isLoading) {
            return;
        }

        // 현재 상태에서 다음 페이지 정보 가져오기
        const boardNo = this.currentBoardNo;
        const cursor = this.currentCursor;
        const loaded = this.currentLoaded;
        const category = this.currentCategory;

        try {
            this.setLoadingState(true);
            this.showLoader();

            // API 호출
            const apiUrl = `/api/product.php?board_no=${boardNo}&cursor=${cursor}&loaded=${loaded}${
                category ? "&category=" + category : ""
            }`;

            const response = await fetch(apiUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || "Failed to load products");
            }

            // 제품 렌더링 (기존 리스트 뒤에 추가)
            if (data.products && data.products.length > 0) {
                const newItems = [];
                const existingNos = this.getExistingProductNos();

                data.products.forEach((product) => {
                    // 중복 체크: 이미 존재하는 제품이거나 cursor 제품이면 제외
                    if (!existingNos.has(product.no) && product.no !== cursor) {
                        const item = this.createProductItem(product, boardNo);
                        this.productList.appendChild(item);
                        newItems.push(item);
                        existingNos.add(product.no);
                    }
                });

                // 애니메이션
                if (newItems.length > 0) {
                    this.animateNewItems(newItems);
                }
            }

            // 상태 업데이트
            this.currentCursor = parseInt(data.next_cursor || "0", 10);
            this.currentLoaded = parseInt(data.current_loaded || "0", 10);
            this.total = parseInt(data.total || "0", 10);

            // 페이지네이션 정보 업데이트
            this.updatePagination(data);

            // 더보기 버튼 업데이트
            this.updateLoadMoreButton(data, boardNo, category);

            // URL 업데이트
            this.updateURL(
                boardNo,
                this.currentCursor,
                this.currentLoaded,
                category
            );
        } catch (error) {
            console.error(
                "[ProductListing] Error loading more products:",
                error
            );
            alert(
                "Fehler beim Laden der Produkte. Bitte versuchen Sie es erneut."
            );
        } finally {
            this.setLoadingState(false);
            this.hideLoader();
        }
    }

    /**
     * 카테고리 변경 시 제품 로드
     * cursor와 loaded를 0으로 초기화하여 첫 페이지부터 로드
     */
    async loadCategory(url) {
        if (this.isLoading) {
            return;
        }

        try {
            this.setLoadingState(true);
            this.showLoader();

            const urlObj = new URL(url, window.location.origin);
            const boardNo = urlObj.searchParams.get("board_no") || "1";
            const category = urlObj.searchParams.get("category") || "";

            // 상태 초기화
            this.currentBoardNo = boardNo;
            this.currentCategory = category;
            this.currentCursor = 0;
            this.currentLoaded = 0;

            // API 호출: cursor=0, loaded=0
            const apiUrl = `/api/product.php?board_no=${boardNo}&cursor=0&loaded=0${
                category ? "&category=" + category : ""
            }`;

            const response = await fetch(apiUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || "Failed to load products");
            }

            // 기존 리스트 비우기 (GSAP 애니메이션 정리)
            const existingItems =
                this.productList.querySelectorAll(".no-products__item");
            existingItems.forEach((item) => {
                gsap.killTweensOf(item);
            });
            this.productList.innerHTML = "";

            // 제품 렌더링
            if (data.products && data.products.length > 0) {
                const newItems = [];
                data.products.forEach((product) => {
                    const item = this.createProductItem(product, boardNo);
                    this.productList.appendChild(item);
                    newItems.push(item);
                });

                // 애니메이션
                if (newItems.length > 0) {
                    this.animateNewItems(newItems);
                }
            }

            // 상태 업데이트
            this.currentCursor = parseInt(data.next_cursor || "0", 10);
            this.currentLoaded = parseInt(data.current_loaded || "0", 10);
            this.total = parseInt(data.total || "0", 10);

            // 페이지네이션 정보 업데이트
            this.updatePagination(data);

            // 필터 제목 업데이트
            this.updateFilterTitle(data.total);

            // 더보기 버튼 업데이트
            this.updateLoadMoreButton(data, boardNo, category);

            // 카테고리 활성 상태 업데이트
            this.updateCategoryActiveState(category);

            // URL 업데이트 (히스토리 변경)
            window.history.pushState(
                { board_no: boardNo, category: category },
                "",
                url
            );
        } catch (error) {
            console.error(
                "[ProductListing] Error loading category products:",
                error
            );
            alert(
                "Fehler beim Laden der Produkte. Bitte versuchen Sie es erneut."
            );
        } finally {
            this.setLoadingState(false);
            this.hideLoader();
        }
    }

    /**
     * 제품 아이템 생성
     * ⚠️ 중요: 상세 페이지 링크는 항상 현재 URL의 cursor/loaded/category만 사용
     * 파라미터로 전달되는 값은 무시 (next_cursor/next_loaded 방지)
     */
    createProductItem(product, boardNo) {
        const li = document.createElement("li");
        li.className = "no-products__item";
        li.setAttribute("data-product-no", product.no);

        // 현재 상태 기준으로 링크 생성
        const cursorValue = this.currentCursor;
        const loadedValue = this.currentLoaded;
        const categoryValue = this.currentCategory;

        let linkUrl = `./view.php?no=${product.no}&board_no=${boardNo}`;
        if (cursorValue) linkUrl += `&cursor=${cursorValue}`;
        if (loadedValue) linkUrl += `&loaded=${loadedValue}`;
        if (categoryValue) linkUrl += `&category=${categoryValue}`;
        linkUrl += `&scroll_to=${product.no}`;

        const link = document.createElement("a");
        link.href = linkUrl;
        link.className = "no-products__link";

        // Image
        const imageDiv = document.createElement("div");
        imageDiv.className = "no-products__image";
        const img = document.createElement("img");
        img.src = `/uploads/board/${product.thumb_image}`;
        img.alt = product.title || "";

        // 이미지 로드 후 가로/세로 비율 확인하여 --hz 클래스 추가
        img.onload = () => {
            this.addHzClassIfNeeded(img);
        };

        // 이미 로드된 경우 (캐시된 이미지)
        if (img.complete) {
            this.addHzClassIfNeeded(img);
        }

        // Hover line 추가
        const hoverLine = document.createElement("div");
        hoverLine.className = "no-products__hover-line";

        imageDiv.appendChild(img);
        imageDiv.appendChild(hoverLine);

        // Content
        const contentDiv = document.createElement("div");
        contentDiv.className = "no-products__content";

        const title = document.createElement("h3");
        title.className = "no-products__title";
        title.textContent = product.title || "";

        contentDiv.appendChild(title);

        // Description (extra1이 있는 경우)
        if (product.extra1) {
            const description = document.createElement("span");
            description.className = "no-products__description";
            description.textContent = `${product.extra2 || ""} ${
                product.extra1
            }`.trim();
            contentDiv.appendChild(description);
        }

        link.appendChild(imageDiv);
        link.appendChild(contentDiv);
        li.appendChild(link);

        return li;
    }

    /**
     * 뒤로 돌아왔을 때 이전 상태 복원
     * scroll_to 제품이 나타날 때까지만 로드 후 스크롤
     */
    async restorePreviousState() {
        const urlParams = new URLSearchParams(window.location.search);
        const productNo = urlParams.get("scroll_to");
        const cursor = urlParams.get("cursor");
        const loaded = urlParams.get("loaded");
        const boardNo = urlParams.get("board_no") || "1";
        const category = urlParams.get("category") || "";

        // 상태 업데이트
        this.currentBoardNo = boardNo;
        this.currentCategory = category;
        this.currentCursor = parseInt(cursor || "0", 10);
        this.currentLoaded = parseInt(loaded || "0", 10);

        console.log(
            `[ProductListing] Restoring state: cursor=${this.currentCursor}, loaded=${this.currentLoaded}, scroll_to=${productNo}`
        );

        // scroll_to 제품이 나타날 때까지만 로드
        if (productNo) {
            await this.loadUntilProduct(productNo, boardNo, category);
        } else if (cursor && loaded) {
            // scroll_to가 없으면 loaded 개수만큼 로드
            const targetLoaded = parseInt(loaded, 10);
            await this.loadUntilCount(targetLoaded, boardNo, category);
        }

        // 제품 로드 완료 후 스크롤 위치 복원
        await new Promise((resolve) => setTimeout(resolve, 500));
        this.restoreScrollPosition(productNo);
    }

    /**
     * 특정 제품이 나타날 때까지 로드
     * scroll_to 제품이 DOM에 나타날 때까지 순차적으로 API 호출
     */
    async loadUntilProduct(productNo, boardNo, category) {
        console.log(
            `[ProductListing] Loading until product appears: ${productNo}`
        );

        // 기존 리스트 비우기
        this.productList.innerHTML = "";

        let currentCursor = 0;
        let currentLoaded = 0;
        let lastData = null;

        // 제품이 나타날 때까지 반복
        while (true) {
            // 제품이 이미 DOM에 있는지 확인
            const productItem = this.productList.querySelector(
                `[data-product-no="${productNo}"]`
            );
            if (productItem) {
                console.log(
                    `[ProductListing] Product ${productNo} found after loading ${currentLoaded} items`
                );
                break;
            }

            try {
                this.setLoadingState(true);
                this.showLoader();

                // API 호출
                const apiUrl = `/api/product.php?board_no=${boardNo}&cursor=${currentCursor}&loaded=${currentLoaded}${
                    category ? "&category=" + category : ""
                }`;

                const response = await fetch(apiUrl);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (!data.success) {
                    throw new Error(data.message || "Failed to load products");
                }

                // 마지막 API 응답 저장
                lastData = data;

                // 제품 렌더링
                if (data.products && data.products.length > 0) {
                    const newItems = [];
                    const existingNos = this.getExistingProductNos();

                    data.products.forEach((product) => {
                        // 중복 체크
                        if (!existingNos.has(product.no)) {
                            const item = this.createProductItem(
                                product,
                                boardNo
                            );
                            this.productList.appendChild(item);
                            newItems.push(item);
                            existingNos.add(product.no);
                        }
                    });

                    // 애니메이션 (모든 새로 로드된 아이템에 적용)
                    if (newItems.length > 0) {
                        this.animateNewItems(newItems);
                    }
                }

                // 제품이 이번 배치에 포함되었는지 다시 확인
                const foundProduct = this.productList.querySelector(
                    `[data-product-no="${productNo}"]`
                );
                if (foundProduct) {
                    console.log(
                        `[ProductListing] Product ${productNo} found in current batch. Loaded: ${data.current_loaded}`
                    );
                    currentLoaded = parseInt(data.current_loaded || "0", 10);
                    break;
                }

                // 다음 cursor와 loaded 업데이트
                const nextCursor = parseInt(data.next_cursor || "0", 10);
                const nextLoaded = parseInt(data.current_loaded || "0", 10);

                // 더 이상 로드할 아이템이 없으면 중단
                if (!data.has_more || nextCursor === 0) {
                    console.log(
                        `[ProductListing] No more products available. Product ${productNo} not found.`
                    );
                    currentLoaded = nextLoaded;
                    break;
                }

                currentCursor = nextCursor;
                currentLoaded = nextLoaded;

                // 로딩 완료 대기
                await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (error) {
                console.error(
                    `[ProductListing] Error loading products at cursor ${currentCursor}:`,
                    error
                );
                break;
            } finally {
                this.setLoadingState(false);
                this.hideLoader();
            }
        }

        // 최종 상태 업데이트
        this.currentCursor = currentCursor;
        this.currentLoaded = currentLoaded;
        if (lastData) {
            this.total = parseInt(lastData.total || "0", 10);
        }

        // 페이지네이션 정보 업데이트
        if (lastData) {
            this.updatePagination(lastData);
        }

        // 더보기 버튼 업데이트
        if (lastData) {
            this.updateLoadMoreButton(lastData, boardNo, category);
        }

        console.log(
            `[ProductListing] Finished loading until product. Total loaded: ${currentLoaded}`
        );
    }

    /**
     * 목표 개수만큼 제품 로드
     * cursor=0부터 시작하여 순차적으로 API 호출하여 targetLoaded 개수만큼 로드
     */
    async loadUntilCount(targetLoaded, boardNo, category) {
        console.log(`[ProductListing] Loading until count: ${targetLoaded}`);

        // 기존 리스트 비우기
        this.productList.innerHTML = "";

        let currentCursor = 0;
        let currentLoaded = 0;
        let lastData = null;

        // 목표 개수에 도달할 때까지 반복
        while (currentLoaded < targetLoaded) {
            try {
                this.setLoadingState(true);
                this.showLoader();

                // API 호출
                const apiUrl = `/api/product.php?board_no=${boardNo}&cursor=${currentCursor}&loaded=${currentLoaded}${
                    category ? "&category=" + category : ""
                }`;

                const response = await fetch(apiUrl);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (!data.success) {
                    throw new Error(data.message || "Failed to load products");
                }

                // 마지막 API 응답 저장 (total 정보용)
                lastData = data;

                // 제품 렌더링
                if (data.products && data.products.length > 0) {
                    const newItems = [];
                    const existingNos = this.getExistingProductNos();

                    data.products.forEach((product) => {
                        // 중복 체크
                        if (!existingNos.has(product.no)) {
                            const item = this.createProductItem(
                                product,
                                boardNo
                            );
                            this.productList.appendChild(item);
                            newItems.push(item);
                            existingNos.add(product.no);
                        }
                    });

                    // 애니메이션 (모든 새로 로드된 아이템에 적용)
                    if (newItems.length > 0) {
                        this.animateNewItems(newItems);
                    }
                }

                // 다음 cursor와 loaded 업데이트
                const nextCursor = parseInt(data.next_cursor || "0", 10);
                const nextLoaded = parseInt(data.current_loaded || "0", 10);

                // 목표 개수에 도달했는지 확인
                if (nextLoaded >= targetLoaded) {
                    console.log(
                        `[ProductListing] Reached target count: ${nextLoaded} >= ${targetLoaded}`
                    );
                    currentLoaded = nextLoaded;
                    break;
                }

                // 더 이상 로드할 아이템이 없다면 중단
                if (!data.has_more) {
                    currentLoaded = nextLoaded;
                    break;
                }

                // nextCursor === 0이어도 loaded < targetLoaded라면 계속 반복해야 함
                if (nextCursor === 0 && nextLoaded >= targetLoaded) {
                    currentLoaded = nextLoaded;
                    break;
                }

                currentCursor = nextCursor;
                currentLoaded = nextLoaded;

                // 로딩 완료 대기
                await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (error) {
                console.error(
                    `[ProductListing] Error loading products at cursor ${currentCursor}:`,
                    error
                );
                break;
            } finally {
                this.setLoadingState(false);
                this.hideLoader();
            }
        }

        // 최종 상태 업데이트
        this.currentCursor = currentCursor;
        this.currentLoaded = currentLoaded;
        if (lastData) {
            this.total = parseInt(lastData.total || "0", 10);
        }

        // 페이지네이션 정보 업데이트
        if (lastData) {
            this.updatePagination(lastData);
        }

        // 더보기 버튼 업데이트
        if (lastData) {
            this.updateLoadMoreButton(lastData, boardNo, category);
        }

        console.log(
            `[ProductListing] Finished loading. Total loaded: ${currentLoaded}`
        );
    }

    /**
     * 스크롤 위치 복원
     * scroll_to 파라미터에 해당하는 제품으로 스크롤
     */
    restoreScrollPosition(productNo) {
        if (!productNo) {
            return;
        }

        // 제품이 DOM에 나타날 때까지 대기
        const checkProduct = () => {
            const productItem = this.productList.querySelector(
                `[data-product-no="${productNo}"]`
            );

            if (productItem) {
                // 약간의 지연을 두어 레이아웃이 안정화되도록 함
                setTimeout(() => {
                    if (this.lenis) {
                        // Lenis를 사용한 스크롤
                        const itemRect = productItem.getBoundingClientRect();
                        const itemTop = itemRect.top + window.scrollY;
                        const windowHeight = window.innerHeight;
                        const itemHeight = itemRect.height;
                        const offset =
                            itemTop - windowHeight / 2 + itemHeight / 2;

                        this.lenis.scrollTo(offset, {
                            duration: 1.2,
                            easing: (t) =>
                                Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                        });
                    } else {
                        // Lenis가 없는 경우 기본 스크롤
                        productItem.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                }, 100);

                // URL에서 scroll_to 파라미터 제거
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.delete("scroll_to");
                const newUrl =
                    window.location.pathname +
                    (urlParams.toString() ? "?" + urlParams.toString() : "");
                window.history.replaceState({}, "", newUrl);
            } else {
                // 제품이 아직 로드되지 않았으면 잠시 후 다시 시도
                setTimeout(checkProduct, 200);
            }
        };

        checkProduct();
    }

    /**
     * URL 업데이트 (히스토리 변경 없이)
     */
    updateURL(boardNo, cursor, loaded, category) {
        const urlParams = new URLSearchParams();
        urlParams.set("board_no", boardNo);
        if (cursor > 0) urlParams.set("cursor", cursor);
        if (loaded > 0) urlParams.set("loaded", loaded);
        if (category) urlParams.set("category", category);

        const newUrl =
            window.location.pathname +
            (urlParams.toString() ? "?" + urlParams.toString() : "");
        window.history.replaceState({}, "", newUrl);
    }

    /**
     * 더보기 버튼 업데이트
     */
    updateLoadMoreButton(data, boardNo, category) {
        // 기존 버튼 제거
        if (this.loadMoreBtn) {
            this.loadMoreBtn.remove();
            this.loadMoreBtn = null;
        }

        // 더보기 버튼이 필요한 경우 생성
        if (data.has_more) {
            const pagination = document.querySelector(
                ".no-products__pagination"
            );
            if (pagination) {
                const moreBtn = document.createElement("a");
                moreBtn.href = `./?board_no=${boardNo}&cursor=${
                    data.next_cursor
                }&loaded=${data.current_loaded}${
                    category ? "&category=" + category : ""
                }`;
                moreBtn.className = "no-products__pagination-more";
                moreBtn.textContent = "Mehr erfahren";

                // 이벤트 바인딩
                moreBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    this.loadMore();
                });

                // 버튼을 pagination-info 앞에 삽입
                const paginationInfo = pagination.querySelector(
                    ".no-products__pagination-info"
                );
                if (paginationInfo) {
                    pagination.insertBefore(moreBtn, paginationInfo);
                } else {
                    pagination.appendChild(moreBtn);
                }

                this.loadMoreBtn = moreBtn;
            }
        }
    }

    /**
     * 카테고리 활성 상태 업데이트
     */
    updateCategoryActiveState(activeCategory) {
        const categoryItems = document.querySelectorAll(
            ".no-products__category-item"
        );
        categoryItems.forEach((item) => {
            const link = item.querySelector("a");
            if (link) {
                const url = new URL(link.href, window.location.origin);
                const category = url.searchParams.get("category") || "";

                if (
                    (activeCategory === "" && category === "") ||
                    activeCategory === category
                ) {
                    item.classList.add("no-products__category-item--active");
                } else {
                    item.classList.remove("no-products__category-item--active");
                }
            }
        });
    }

    /**
     * 필터 제목 업데이트
     */
    updateFilterTitle(total) {
        const filterTitle = document.querySelector(
            ".no-products__filter-title span"
        );
        if (filterTitle) {
            filterTitle.textContent = `(${total})`;
        }
    }

    /**
     * 페이지네이션 정보 업데이트
     */
    updatePagination(data) {
        if (!this.paginationInfo) return;

        const currentSpan = this.paginationInfo.querySelector(
            ".no-products__pagination-current"
        );
        const totalSpan = this.paginationInfo.querySelector(
            ".no-products__pagination-total"
        );

        if (currentSpan) {
            currentSpan.textContent = data.current_loaded;
        }

        if (totalSpan) {
            totalSpan.textContent = data.total;
        }

        if (this.paginationProgress) {
            const progressPercent =
                data.total > 0 ? (data.current_loaded / data.total) * 100 : 0;
            this.paginationProgress.style.width = progressPercent + "%";
        }
    }

    /**
     * 기존 제품 번호 수집 (중복 체크용)
     */
    getExistingProductNos() {
        const existingNos = new Set();
        const items = this.productList.querySelectorAll(".no-products__item");
        items.forEach((item) => {
            const productNo = item.getAttribute("data-product-no");
            if (productNo) {
                existingNos.add(parseInt(productNo, 10));
            }
        });
        return existingNos;
    }

    /**
     * 로딩 상태 설정
     */
    setLoadingState(isLoading) {
        this.isLoading = isLoading;

        // 카테고리 링크들 비활성화/활성화
        const categoryLinks = document.querySelectorAll(
            ".no-products__category a"
        );
        categoryLinks.forEach((link) => {
            if (isLoading) {
                link.style.pointerEvents = "none";
                link.style.opacity = "0.5";
            } else {
                link.style.pointerEvents = "";
                link.style.opacity = "";
            }
        });

        if (this.loadMoreBtn) {
            if (isLoading) {
                this.loadMoreBtn.disabled = true;
                this.loadMoreBtn.innerHTML = `
                    <i class="fa-regular fa-loader"></i>
                    <span>Wird geladen...</span>
                `;
                this.loadMoreBtn.classList.add("is-loading");
            } else {
                this.loadMoreBtn.disabled = false;
                this.loadMoreBtn.innerHTML = "Mehr erfahren";
                this.loadMoreBtn.classList.remove("is-loading");
            }
        }
    }

    /**
     * 로더 표시
     */
    showLoader() {
        if (this.loader) {
            this.loader.style.display = "flex";
        }
    }

    /**
     * 로더 숨김
     */
    hideLoader() {
        if (this.loader) {
            this.loader.style.display = "none";
        }
    }

    /**
     * 새 아이템들 애니메이션
     */
    animateNewItems(items) {
        if (items.length === 0) return;

        // 새 아이템들을 초기 상태로 설정
        gsap.set(items, {
            opacity: 0,
            y: 30,
            scale: 0.95,
        });

        // 순차적으로 나타나는 애니메이션
        gsap.to(items, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.5,
            ease: "power3.out",
            stagger: {
                amount: 0.3,
                from: "start",
            },
        });
    }

    /**
     * 이미지 가로/세로 비율 확인하여 --hz 클래스 추가
     */
    addHzClassIfNeeded(img) {
        if (img.naturalWidth > img.naturalHeight) {
            const imageDiv = img.closest(".no-products__image");
            if (imageDiv) {
                imageDiv.classList.add("--hz");
            }
        }
    }

    /**
     * 기존 DOM 요소들의 opacity를 1로 설정 (AJAX 미사용 시)
     */
    setExistingItemsOpacity() {
        if (!this.productList) return;

        const existingItems =
            this.productList.querySelectorAll(".no-products__item");

        existingItems.forEach((item) => {
            item.style.opacity = "1";
        });

        console.log(
            `[ProductListing] Set opacity to 1 for ${existingItems.length} existing items`
        );
    }

    /**
     * 카테고리 이벤트 바인딩
     */
    bindCategoryEvents() {
        const categoryLinks = document.querySelectorAll(
            ".no-products__category a"
        );
        categoryLinks.forEach((link) => {
            link.addEventListener("click", (e) => {
                e.preventDefault();
                this.loadCategory(link.href);
            });
        });
    }
}
